/**
 * ARCADIA '84 — shell entry.
 *
 * Boot order:
 * 1. Find the canvas + 2D context.
 * 2. Run the CRT power-on boot animation (sc-2775).
 * 3. Show the cartridge selector (sc-2818). Player picks a cartridge.
 * 4. Hand off to the launcher; it runs the cartridge until the cartridge
 *    fires ctx.exit(). Loop returns to step 3.
 * 5. Wire up the PWA install funnel (sc-2776) before the selector shows.
 */

import './styles/tokens.css';
import './styles/shell.css';
import { runCallbackIfNeeded } from './auth/callback.js';
import { runBillingReturnIfNeeded } from './billing/return.js';
import { wireFooterSession } from './auth/footer-session.js';
import { refreshFooterCredits, wireFooterCredits } from './runtime/footer-credit.js';
import { identify, initAnalytics, track } from './runtime/analytics.js';
import { getSession } from '@arcraz/arcadia-auth-web';
import { runBootSequence } from './runtime/boot.js';
import { mountCabinetAd } from './runtime/cabinet-ad.js';
import {
  createInstallController,
  type InstallController,
  type InstallStatus,
} from './runtime/install.js';
import { getCabinetAudioBus, isTouch } from './runtime/context.js';
import { KeyboardInputSource } from './runtime/input-keyboard.js';
import { attachBezelFit } from './runtime/bezel-fit.js';
import { attachCompositor, getOffscreenContext } from './runtime/compositor.js';
import { setCabinetMode } from './runtime/cabinet-mode.js';
import { launchCartridge } from './runtime/launcher.js';
import { runModePicker, type Mode } from './runtime/mode-picker.js';
import { attachOrientationController } from './runtime/orientation.js';
import { runSelector } from './runtime/selector.js';
import { resolvePlayAccess } from './runtime/play-access.js';
import {
  consumePendingLaunch,
  setPendingLaunch,
  showLoginWall,
  showPaywall,
} from './runtime/play-gate-wall.js';
import { CARTRIDGES } from './cartridges/registry.js';

function wireInstallFunnel(controller: InstallController): void {
  const banner = document.getElementById('install-banner');
  const acceptBtn = document.getElementById('install-banner-accept');
  const dismissBtn = document.getElementById('install-banner-dismiss');
  const overlay = document.getElementById('install-overlay');
  const overlayDismiss = document.getElementById('install-overlay-dismiss');

  if (
    !(banner instanceof HTMLElement) ||
    !(acceptBtn instanceof HTMLButtonElement) ||
    !(dismissBtn instanceof HTMLButtonElement) ||
    !(overlay instanceof HTMLElement) ||
    !(overlayDismiss instanceof HTMLButtonElement)
  ) {
    console.warn('[arcadia84] install funnel: DOM nodes missing — skipping');
    return;
  }

  // Track which surfaces we've already reported to avoid spamming the
  // event log on every status change (controller.subscribe fires for
  // every internal state transition, including no-op re-renders).
  const seenSurfaces = new Set<string>();

  const render = (status: InstallStatus): void => {
    // Only surface the install banner on touch devices (tablets, phones).
    // Desktop Chrome fires `beforeinstallprompt` too, but installing a
    // landscape-only cabinet PWA on a desktop is noise — keep that funnel
    // hidden until / unless the player is on a device where pocketing
    // the cabinet makes sense. iOS gets the same gate: the manual
    // overlay only matters on actual iOS touch devices.
    const showBanner = status.surface === 'banner' && isTouch();
    const showOverlay = status.surface === 'ios-instructions' && isTouch();
    banner.hidden = !showBanner;
    overlay.hidden = !showOverlay;

    if (showBanner && !seenSurfaces.has('banner')) {
      seenSurfaces.add('banner');
      track('install_banner_shown', { surface: 'banner' });
    }
    if (showOverlay && !seenSurfaces.has('ios-instructions')) {
      seenSurfaces.add('ios-instructions');
      track('install_banner_shown', { surface: 'ios-instructions' });
    }
  };

  acceptBtn.addEventListener('click', () => {
    track('install_accepted', { surface: 'banner' });
    void controller.install().then((outcome) => {
      console.info('[arcadia84] install outcome', { outcome });
      track('install_outcome', { outcome });
    });
  });

  dismissBtn.addEventListener('click', () => {
    track('install_dismissed', { surface: 'banner' });
    controller.dismiss();
  });

  overlayDismiss.addEventListener('click', () => {
    track('install_dismissed', { surface: 'ios-instructions' });
    controller.dismiss();
  });

  render(controller.getStatus());
  controller.subscribe(render);
}

/**
 * Update the cabinet's control-panel title to reflect the active cartridge.
 */
function setCabinetTitle(title: string): void {
  const el = document.getElementById('cabinet-title');
  if (el) {
    el.textContent = title;
  }
}

/**
 * Top-level cabinet loop: selector → cartridge → selector → ...
 *
 * The cabinet doesn't replay the boot animation between cartridges (that
 * was a Sprint 0 question — boot is "cabinet powers on," not "every game
 * starts"). The selector is the persistent post-boot home. Player exits a
 * cartridge → lands back on the selector to pick again.
 */
async function runCabinet(
  ctx2d: CanvasRenderingContext2D,
  input: KeyboardInputSource,
  pendingCartridgeId?: string | null
): Promise<void> {
  // sc-6940: after a sign-in round-trip (Google / magic-link), the player
  // was mid-launch of a specific cartridge. Skip the selector for that one
  // iteration and take them straight back to the gate for it — now signed
  // in, the gate spends a token and grants play. Mode isn't stashed across
  // the redirect, so the relaunch is NORMAL mode (matches the remaster).
  let pending = pendingCartridgeId
    ? (CARTRIDGES.find((c) => c.id === pendingCartridgeId) ?? null)
    : null;

  for (;;) {
    // Selector lives in attract mode — marquee + control panel visible.
    setCabinetMode('attract');
    setCabinetTitle('SELECT GAME');

    let module_;
    let mode: Mode = 'normal';
    if (pending) {
      module_ = pending;
      pending = null;
      track('pending_relaunch', { cartridgeId: module_.id });
    } else {
      const picked = await runSelector(ctx2d, input, CARTRIDGES);
      module_ = picked.module_;
      track('cartridge_selected', {
        cartridgeId: module_.id,
        cartridgeTitle: module_.title,
      });

      // Mode picker (sc-3016): NORMAL vs DAILY CHALLENGE. Escape backs
      // out to the selector for another pick.
      const choice = await runModePicker(ctx2d, input, module_);
      if (choice.kind === 'back') {
        continue;
      }
      mode = choice.mode;
    }

    // Play-access gate (Monetization v2, sc-6940). Every launch routes
    // through the server-authoritative gate BEFORE the cartridge mounts —
    // parity with the remaster. A non-'play' verdict shows the wall/paywall
    // and returns to the selector; the player never sees PRESS START for a
    // run they can't play.
    const access = await passesPlayGate(module_.id, module_.title);
    if (!access) {
      continue;
    }

    track('cartridge_started', {
      cartridgeId: module_.id,
      cartridgeTitle: module_.title,
      mode,
    });
    if (mode === 'daily') {
      track('daily_challenge_started', {
        cartridgeId: module_.id,
        dailySeedDate: todayUtcDate(),
      });
    }

    const startedAt = performance.now();
    const exitReason = await launchCartridge(module_, ctx2d, input, {
      onTitle: (t) => setCabinetTitle(t),
      registerPauseRequest: registerEscapePauseListener,
      // sc-7036: only a signed-in token run spends at first-round start.
      // A pass is unlimited; the anon free-taste was consumed at the gate.
      spendTokenOnStart: access === 'token',
      ...(mode === 'daily' ? { dailyChallengeDate: todayUtcDate() } : {}),
    });
    track('cartridge_exited', {
      cartridgeId: module_.id,
      mode,
      exitReason,
      durationMs: Math.round(performance.now() - startedAt),
    });
    // A token run just ended (or a pass lapsed) — repaint the footer credit
    // so the count reflects the spend before the player picks again.
    void refreshFooterCredits();
    if (mode === 'daily' && exitReason === 'game_over') {
      track('daily_challenge_completed', {
        cartridgeId: module_.id,
        dailySeedDate: todayUtcDate(),
      });
    }
    // launchCartridge flips back to attract mode on exit; loop iterates.
  }
}

/**
 * Run the play-access gate for one launch (sc-6940; spend-timing sc-7036).
 * Returns the granted `access` ('pass' | 'token' | 'free-taste') when the
 * player may play, or `null` after showing the login wall / paywall — the
 * caller returns to the selector in that case.
 *
 * The gate only CHECKS eligibility. The signed-in TOKEN is NOT spent here;
 * it's spent at the first round's INSERT COIN inside launchCartridge
 * (sc-7036), so backing out of press-start or a cartridge that fails to
 * init costs the player nothing. The returned access tells the launcher
 * whether to spend at start ('token') or not ('pass'/'free-taste').
 *
 * The wall's auth actions (Google redirect, magic-link email) navigate
 * away; those promises never resolve here. Only the "Maybe later" /
 * "Back to cabinet" dismiss resolves, and it resolves to `null`.
 */
async function passesPlayGate(
  cartridgeId: string,
  cartridgeTitle: string
): Promise<'pass' | 'token' | 'free-taste' | null> {
  const verdict = await resolvePlayAccess();
  switch (verdict.grant) {
    case 'play':
      return verdict.access;
    case 'login':
      track('login_wall_shown', { cartridgeId });
      // onSignedIn fires only for the in-place email-OTP path (sc-7039).
      // The classic cabinet resumes via a boot-time pending relaunch (like
      // the Google/magic-link callback path), so stash the cartridge and
      // reload — runCabinet picks it up via consumePendingLaunch on boot.
      await showLoginWall(cartridgeId, cartridgeTitle, () => {
        if (cartridgeId) setPendingLaunch(cartridgeId);
        window.location.reload();
      });
      return null;
    case 'paywall':
      track('paywall_shown', { cartridgeId });
      // Pass the cartridge so a purchase relaunches it via /billing/return.
      await showPaywall(verdict.resetsAtUtc, cartridgeId);
      return null;
    case 'error':
    default:
      // Wallet read threw. Treat like the paywall (never a free run) but
      // without a reset time — the wall just states the situation + passes.
      track('play_gate_error', { cartridgeId });
      await showPaywall(undefined, cartridgeId);
      return null;
  }
}

function todayUtcDate(): string {
  // YYYY-MM-DD in UTC — matches the server's daily-seed convention.
  return new Date().toISOString().slice(0, 10);
}

/**
 * Window-level Escape listener. While a cartridge is running, the first
 * Escape press fires `requestPause`; the launcher pauses the runtime
 * and shows the pause overlay. Subsequent Escape presses are read by
 * the overlay (via the `coin` button mapping) to resume.
 *
 * The launcher passes us a `requestPause` callback unique to the current
 * cartridge run. We return an unregister function it calls in its
 * `finally` block to detach the listener cleanly.
 */
function registerEscapePauseListener(requestPause: () => void): () => void {
  // Throttle re-fires to ~1/sec — the overlay handles its own Escape via
  // the input source's `coin` justPressed. This listener only matters
  // for "open the overlay from gameplay."
  let lastFire = 0;
  const handler = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape') return;
    // Ignore Escape from inside a text input — the login modal (and any
    // future text-entry overlay) handles its own Esc-to-close behavior.
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    const now = performance.now();
    if (now - lastFire < 500) return;
    lastFire = now;
    requestPause();
  };
  window.addEventListener('keydown', handler);
  return () => {
    window.removeEventListener('keydown', handler);
  };
}

async function boot(): Promise<void> {
  // Analytics seam first, so auth-callback landings and install-funnel
  // surfaces are covered from boot. Currently a no-op — see
  // runtime/analytics.ts; run telemetry goes to our own API.
  initAnalytics();

  // sc-3015: handle /auth/callback before anything else. On a successful
  // exchange, the session is stashed and the URL is rewritten to `/` —
  // the cabinet boots normally below with the player already signed in.
  // On a rendered error (bad/expired token), the callback handler shows
  // its own error card and we bail without booting the cabinet.
  const callback = await runCallbackIfNeeded();
  if (callback.handled) {
    track('auth_callback_handled', { signedIn: callback.signedIn });
  }
  if (callback.handled && !callback.signedIn) {
    return;
  }

  // sc-6943: handle a PayPal return/cancel before booting. Captures the
  // day-pass order + waits for the entitlement, then rewrites the URL to
  // `/`. The pending cartridge stashed before the redirect relaunches via
  // the boot-time consumePendingLaunch() below (the gate re-checks access
  // and now sees the active pass).
  const billing = await runBillingReturnIfNeeded();
  if (billing.handled) {
    track('billing_return_handled', { entitled: billing.entitled });
  }

  // If we have a session (fresh from this callback OR persisted from a
  // prior visit), identify the player to the analytics seam so future
  // events attach to the user, not just an anonymous id.
  const session = getSession();
  if (session) {
    identify(session.userId, { email: session.email });
    if (callback.signedIn) {
      track('auth_signin_completed', { userId: session.userId });
    }
  }

  const canvas = document.getElementById('game');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('Cabinet missing #game canvas — index.html is broken.');
  }

  // Bezel sizing — write inline 16:9 pixel dimensions on every resize.
  // Must run BEFORE the compositor attaches so the visible canvas's
  // first ResizeObserver tick reads the correct CSS box. Without this,
  // pure-CSS aspect-ratio sizing fights with max-* clamps and resolves
  // to non-16:9 at many viewport aspects (verified 2026-05-17).
  attachBezelFit();

  // Compositor owns two surfaces: the visible #game canvas (sized to the
  // bezel × devicePixelRatio so the blit lands on native pixels) and an
  // off-screen canvas at the cabinet's logical resolution. All shell +
  // cartridge code renders into the off-screen ctx; the compositor blits
  // it onto the visible canvas every frame (nearest-neighbor for pixel
  // cartridges, bilinear for vector). Eliminates the fractional-pixel
  // artifacts of `image-rendering: pixelated` on the visible canvas.
  attachCompositor(canvas, 480, 270);
  const ctx = getOffscreenContext();

  // Spin up the install controller before the boot animation finishes —
  // the browser may fire beforeinstallprompt early and we don't want to
  // miss the event window.
  const installController = createInstallController();

  // Orientation controller attaches before boot so the rotate-to-play
  // overlay can appear immediately if the user lands on portrait.
  attachOrientationController();

  // Keyboard input attaches once at cabinet boot; survives across cartridge
  // teardowns / restarts.
  const input = new KeyboardInputSource();
  input.attach();

  // Cabinet-level mute toggle. M flips audio mute regardless of cartridge
  // state — works during gameplay, in pause overlay, on the selector, on
  // game-over. Single source of truth: the shared audio bus.
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyM') return;
    // Ignore if a text input has focus (none in the cabinet today, but
    // safe default for any future settings overlay).
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const audio = getCabinetAudioBus();
    audio.setMuted(!audio.getMuted());
    e.preventDefault();
  });

  // Release the shared AudioContext when the page unloads. Without this,
  // Chrome's CoreAudio output unit can stay claimed after the tab is
  // closed or refreshed during dev — manifesting on the host OS as
  // "no audio out of my machine" until `sudo killall coreaudiod`.
  // `pagehide` fires on tab close, navigation, AND background-tab in
  // mobile Safari; `beforeunload` is the older sibling for legacy paths.
  // `audio.destroy()` calls ctx.close() which releases the device cleanly.
  const releaseAudio = (): void => {
    try {
      getCabinetAudioBus().destroy();
    } catch {
      /* best-effort — already-closed contexts throw on close */
    }
  };
  window.addEventListener('pagehide', releaseAudio);
  window.addEventListener('beforeunload', releaseAudio);

  const result = await runBootSequence(ctx);
  wireInstallFunnel(installController);
  wireFooterSession();
  wireFooterCredits();
  mountCabinetAd();

  console.info('[arcadia84] cabinet booted', {
    canvas: `${canvas.width}x${canvas.height}`,
    skipped: result.skipped,
    install: installController.getStatus().surface,
  });

  // sc-6940 / sc-7039: if the player was mid-launch of a cartridge before
  // signing in, relaunch it. Consume UNCONDITIONALLY — two auth paths land
  // here: (1) the redirect flows (Google/magic-link) come back through
  // /auth/callback with signedIn=true, and (2) the in-place email-OTP flow
  // stashes the cartridge then reloads the PLAIN page (not /auth/callback),
  // so callback.signedIn is false on that boot. Gating the consume on
  // callback.signedIn dropped the OTP pending cartridge (sc-7039 review).
  // consumePendingLaunch() is a harmless read+clear when nothing's stashed,
  // and runCabinet re-checks access at the gate regardless, so consuming a
  // stale stash is safe.
  const pendingCartridgeId = consumePendingLaunch();
  await runCabinet(ctx, input, pendingCartridgeId);
}

void boot();

export {};
